import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, Users, Plus, ChevronLeft, ChevronRight, CheckCircle, XCircle, HelpCircle, Edit, Trash2, Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon, Download } from 'lucide-react';
import { useMeetings, useCreateMeeting, useUpdateMeeting, useDeleteMeeting, useMeetingAttendances, useBulkUpdateAttendances } from '../hooks/useMeetings.ts';
import CallRegisterModal from '../components/CallRegisterModal';
import { useWorkflowSettings } from '../hooks/useWorkflowSettings.ts';
import { useMembers } from '../hooks/useMembers.ts';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import Modal from '../components/Modal.tsx';
import { toast } from 'react-hot-toast';
import { PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../utils/axios.ts';
import jsPDF from 'jspdf';

const MeetingsPageNew = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('month'); // month, week, list
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [activeTab, setActiveTab] = useState('details'); // details, attendees, minutes
  const [showAllMeetings, setShowAllMeetings] = useState(false); // Toggle for showing all meetings
  
  const { can } = usePermissions();
  const isAdmin = useIsAdmin();
  const { data: meetingsData, isLoading } = useMeetings();
  const { data: membersData } = useMembers();
  const createMeeting = useCreateMeeting();
  const updateMeeting = useUpdateMeeting();
  const deleteMeeting = useDeleteMeeting();
  const bulkUpdateAttendances = useBulkUpdateAttendances();

  const meetings = meetingsData?.data || [];
  const members = membersData?.data || [];

  // Fetch attendances for selected meeting
  const { data: attendancesData } = useMeetingAttendances(
    selectedMeeting ? selectedMeeting.id : null
  );
  const attendances = attendancesData?.data || [];

  // Calendar navigation
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get meetings for current view
  const visibleMeetings = useMemo(() => {
    if (showAllMeetings) {
      return meetings; // Show all meetings regardless of date
    }
    
    return meetings.filter(meeting => {
      const meetingDate = new Date(meeting.meeting_date);
      const current = new Date(currentDate);
      
      if (view === 'month') {
        return meetingDate.getMonth() === current.getMonth() &&
               meetingDate.getFullYear() === current.getFullYear();
      } else if (view === 'week') {
        const weekStart = new Date(current);
        weekStart.setDate(current.getDate() - current.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return meetingDate >= weekStart && meetingDate <= weekEnd;
      }
      return true;
    });
  }, [meetings, currentDate, view, showAllMeetings]);

  const openMeetingDetails = (meeting) => {
    setSelectedMeeting(meeting);
    setShowDetailsModal(true);
    setActiveTab('details');
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
      ongoing: 'bg-green-100 text-green-700 border-green-200',
      completed: 'bg-gray-100 text-gray-700 border-gray-200',
      cancelled: 'bg-red-100 text-red-700 border-red-200',
    };
    return colors[status] || colors.scheduled;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile Header */}
          <div className="lg:hidden py-4">
            <div className="flex items-center justify-between mb-3">
              <h1 className="text-xl font-semibold text-gray-900">Meetings</h1>
              {isAdmin && (
                <PermissionGate permission={PERMISSIONS.CREATE_MEETING}>
                  <button
                    onClick={() => navigate('/meetings/create')}
                    className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
                  >
                    <Plus size={18} className="mr-1" />
                    Create
                  </button>
                </PermissionGate>
              )}
            </div>
            
            {/* Mobile Navigation */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <button
                  onClick={goToPrevious}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="text-base font-medium text-gray-900">
                  {currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </span>
                <button
                  onClick={goToNext}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
              <button
                onClick={goToToday}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Today
              </button>
            </div>
            
            {/* Mobile View Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setView('month')}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  view === 'month' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                }`}
              >
                Month
              </button>
              <button
                onClick={() => setView('week')}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  view === 'week' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                }`}
              >
                Week
              </button>
              <button
                onClick={() => setView('list')}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  view === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                }`}
              >
                List
              </button>
            </div>
            
            {/* Mobile Show All Toggle */}
            {view !== 'list' && (
              <label className="flex items-center space-x-2 text-xs text-gray-700 cursor-pointer mt-3">
                <input
                  type="checkbox"
                  checked={showAllMeetings}
                  onChange={(e) => setShowAllMeetings(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span>Show All Meetings</span>
              </label>
            )}
          </div>
          
          {/* Desktop Header */}
          <div className="hidden lg:flex items-center justify-between h-16">
            {/* Left: Title and Navigation */}
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-semibold text-gray-900">Meetings</h1>
              
              <div className="flex items-center space-x-2 ml-8">
                <button
                  onClick={goToToday}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Today
                </button>
                <button
                  onClick={goToPrevious}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={goToNext}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <ChevronRight size={20} />
                </button>
                <span className="text-lg font-medium text-gray-900 ml-2">
                  {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
              </div>
            </div>

            {/* Right: View Toggle and Create Button */}
            <div className="flex items-center space-x-3">
              {/* Show All Toggle */}
              {view !== 'list' && (
                <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showAllMeetings}
                    onChange={(e) => setShowAllMeetings(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span>Show All Meetings</span>
                </label>
              )}
              
              {/* View Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setView('month')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    view === 'month' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Month
                </button>
                <button
                  onClick={() => setView('week')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    view === 'week' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Week
                </button>
                <button
                  onClick={() => setView('list')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    view === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  List
                </button>
              </div>

              {/* Create Meeting Button */}
              {isAdmin && (
                <PermissionGate permission={PERMISSIONS.CREATE_MEETING}>
                  <button
                    onClick={() => navigate('/meetings/create')}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    <Plus size={20} className="mr-2" />
                    Create
                  </button>
                </PermissionGate>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : view === 'list' ? (
          <ListView meetings={visibleMeetings} onMeetingClick={openMeetingDetails} />
        ) : view === 'month' ? (
          <MonthView 
            meetings={visibleMeetings} 
            currentDate={currentDate}
            onMeetingClick={openMeetingDetails}
          />
        ) : (
          <WeekView 
            meetings={visibleMeetings} 
            currentDate={currentDate}
            onMeetingClick={openMeetingDetails}
          />
        )}
      </div>

      {/* Create Meeting Modal */}
      <CreateMeetingModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={createMeeting}
        members={members}
      />

      {/* Meeting Details Modal */}
      {selectedMeeting && (
        <MeetingDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedMeeting(null);
          }}
          meeting={selectedMeeting}
          members={members}
          attendances={attendances}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onUpdate={updateMeeting}
          onDelete={deleteMeeting}
          onUpdateAttendances={bulkUpdateAttendances}
          isAdmin={isAdmin}
          can={can}
        />
      )}
    </div>
  );
};

// List View Component
const ListView = ({ meetings, onMeetingClick }) => {
  if (meetings.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
        <p className="text-gray-500">No meetings scheduled</p>
      </div>
    );
  }

  // Group meetings by date
  const groupedMeetings = meetings.reduce((acc, meeting) => {
    const date = new Date(meeting.meeting_date).toDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(meeting);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {Object.entries(groupedMeetings).map(([date, dayMeetings]) => (
        <div key={date}>
          <h3 className="text-sm font-semibold text-gray-500 mb-3">
            {new Date(date).toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'long', 
              day: 'numeric' 
            })}
          </h3>
          <div className="space-y-2">
            {dayMeetings.map(meeting => (
              <MeetingCard key={meeting.id} meeting={meeting} onClick={() => onMeetingClick(meeting)} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Meeting Card Component
const MeetingCard = ({ meeting, onClick }) => {
  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'border-l-blue-500',
      ongoing: 'border-l-green-500',
      completed: 'border-l-gray-400',
      cancelled: 'border-l-red-500',
    };
    return colors[status] || colors.scheduled;
  };

  return (
    <div
      onClick={onClick}
      className={`bg-white border-l-4 ${getStatusColor(meeting.status)} rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer p-4`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="text-lg font-semibold text-gray-900 mb-1">{meeting.title}</h4>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center">
              <Clock size={16} className="mr-1.5" />
              {meeting.meeting_time || meeting.start_time || 'TBD'}
            </div>
            {meeting.location && (
              <div className="flex items-center">
                <MapPin size={16} className="mr-1.5" />
                {meeting.location}
              </div>
            )}
            <div className="flex items-center">
              <Users size={16} className="mr-1.5" />
              {meeting.attendance_stats?.total || 0} attendees
            </div>
          </div>
          {meeting.description && (
            <p className="text-sm text-gray-600 mt-2 line-clamp-2">{meeting.description}</p>
          )}
        </div>
        <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
          meeting.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
          meeting.status === 'ongoing' ? 'bg-green-100 text-green-700' :
          meeting.status === 'completed' ? 'bg-gray-100 text-gray-700' :
          'bg-red-100 text-red-700'
        }`}>
          {meeting.status}
        </span>
      </div>
    </div>
  );
};

// Month View Component with Calendar Grid
const MonthView = ({ meetings, currentDate, onMeetingClick }) => {
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getMeetingsForDay = (date) => {
    if (!date) return [];
    return meetings.filter(meeting => {
      const meetingDate = new Date(meeting.meeting_date);
      return meetingDate.getDate() === date.getDate() &&
             meetingDate.getMonth() === date.getMonth() &&
             meetingDate.getFullYear() === date.getFullYear();
    });
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const days = getDaysInMonth();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="bg-white rounded-lg shadow overflow-x-auto">
      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-px bg-gray-200 sm:min-w-[640px]">
        {/* Week day headers */}
        {weekDays.map(day => (
          <div key={day} className="bg-gray-50 p-2 text-center text-xs sm:text-sm font-semibold text-gray-700">
            <span className="hidden sm:inline">{day}</span>
            <span className="sm:hidden">{day.slice(0, 1)}</span>
          </div>
        ))}
        
        {/* Calendar days */}
        {days.map((date, index) => {
          const dayMeetings = getMeetingsForDay(date);
          const isCurrentDay = isToday(date);
          
          return (
            <div
              key={index}
              className={`bg-white min-h-[56px] sm:min-h-[120px] p-1 sm:p-2 ${
                !date ? 'bg-gray-50' : ''
              } ${isCurrentDay ? 'bg-blue-50' : ''}`}
            >
              {date && (
                <>
                  <div className={`text-xs sm:text-sm font-medium mb-1 ${
                    isCurrentDay ? 'text-blue-600 font-bold' : 'text-gray-700'
                  }`}>
                    {date.getDate()}
                  </div>
                  <div className="space-y-1">
                    {dayMeetings.slice(0, 2).map(meeting => (
                      <div
                        key={meeting.id}
                        onClick={() => onMeetingClick(meeting)}
                        className={`text-[10px] sm:text-xs p-0.5 sm:p-1 rounded cursor-pointer hover:opacity-80 truncate ${
                          meeting.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                          meeting.status === 'ongoing' ? 'bg-green-100 text-green-700' :
                          meeting.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                          'bg-red-100 text-red-700'
                        }`}
                        title={meeting.title}
                      >
                        <span className="hidden sm:inline">
                          {meeting.start_time && (
                            <span className="font-semibold">{meeting.start_time.slice(0, 5)} </span>
                          )}
                        </span>
                        {meeting.title}
                      </div>
                    ))}
                    {dayMeetings.length > 2 && (
                      <div className="text-[10px] sm:text-xs text-gray-500 pl-0.5 sm:pl-1">
                        +{dayMeetings.length - 2}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Week View Component with Time Slots
const WeekView = ({ meetings, currentDate, onMeetingClick }) => {
  const getWeekDays = () => {
    const weekStart = new Date(currentDate);
    weekStart.setDate(currentDate.getDate() - currentDate.getDay());
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getMeetingsForDay = (date) => {
    return meetings.filter(meeting => {
      const meetingDate = new Date(meeting.meeting_date);
      return meetingDate.getDate() === date.getDate() &&
             meetingDate.getMonth() === date.getMonth() &&
             meetingDate.getFullYear() === date.getFullYear();
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const weekDays = getWeekDays();
  const weekDayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <div className="bg-white rounded-lg shadow overflow-x-auto">
      <div className="grid grid-cols-7 gap-px bg-gray-200 sm:min-w-[640px]">
        {weekDays.map((date, index) => {
          const dayMeetings = getMeetingsForDay(date);
          const isCurrentDay = isToday(date);
          
          return (
            <div key={index} className={`bg-white p-2 sm:p-4 min-h-[300px] sm:min-h-[400px] ${isCurrentDay ? 'bg-blue-50' : ''}`}>
              <div className="text-center mb-2 sm:mb-4">
                <div className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase">
                  <span className="hidden sm:inline">{weekDayNames[index].slice(0, 3)}</span>
                  <span className="sm:hidden">{weekDayNames[index].slice(0, 1)}</span>
                </div>
                <div className={`text-lg sm:text-2xl font-bold mt-1 ${
                  isCurrentDay ? 'text-blue-600' : 'text-gray-900'
                }`}>
                  {date.getDate()}
                </div>
                <div className="text-[10px] sm:text-xs text-gray-500">
                  {date.toLocaleDateString('en-US', { month: 'short' })}
                </div>
              </div>
              
              <div className="space-y-1 sm:space-y-2">
                {dayMeetings.length === 0 ? (
                  <div className="text-center text-gray-400 text-xs sm:text-sm py-4 sm:py-8">
                    No meetings
                  </div>
                ) : (
                  dayMeetings.map(meeting => (
                    <div
                      key={meeting.id}
                      onClick={() => onMeetingClick(meeting)}
                      className={`p-2 sm:p-3 rounded-lg cursor-pointer hover:shadow-md transition-shadow ${
                        meeting.status === 'scheduled' ? 'bg-blue-100 border-l-4 border-blue-500' :
                        meeting.status === 'ongoing' ? 'bg-green-100 border-l-4 border-green-500' :
                        meeting.status === 'completed' ? 'bg-gray-100 border-l-4 border-gray-400' :
                        'bg-red-100 border-l-4 border-red-500'
                      }`}
                    >
                      <div className="font-semibold text-xs sm:text-sm text-gray-900 mb-1">
                        {meeting.start_time && (
                          <span className="text-[10px] sm:text-xs font-bold mr-1">{meeting.start_time.slice(0, 5)}</span>
                        )}
                        <span className="line-clamp-2">{meeting.title}</span>
                      </div>
                      {meeting.location && (
                        <div className="text-[10px] sm:text-xs text-gray-600 flex items-center truncate">
                          <MapPin size={10} className="mr-1 flex-shrink-0 hidden sm:block" />
                          {meeting.location}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Create Meeting Modal Component
const CreateMeetingModal = ({ isOpen, onClose, onCreate, members }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    meeting_date: new Date().toISOString().slice(0, 10),
    start_time: '',
    location: '',
    agenda: '',
    type: 'regular',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onCreate.mutateAsync(form);
      toast.success('Meeting created successfully');
      onClose();
      setForm({
        title: '',
        description: '',
        meeting_date: new Date().toISOString().slice(0, 10),
        start_time: '',
        location: '',
        agenda: '',
        type: 'regular',
      });
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to create meeting';
      const validationErrors = error?.response?.data?.errors;
      if (validationErrors) {
        const firstError = Object.values(validationErrors)[0][0];
        toast.error(`Validation failed: ${firstError}`);
      } else {
        toast.error(errorMessage);
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Create Meeting</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Monthly Team Meeting"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Discuss quarterly goals and team updates..."
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                value={form.meeting_date}
                onChange={e => setForm(f => ({ ...f, meeting_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                required
                value={form.start_time}
                onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              value={form.location}
              onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Conference Room A or Zoom link"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={form.type}
              onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="regular">Regular</option>
              <option value="emergency">Emergency</option>
              <option value="annual">Annual</option>
              <option value="special">Special</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Agenda</label>
            <textarea
              value={form.agenda}
              onChange={e => setForm(f => ({ ...f, agenda: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="1. Review last meeting minutes&#10;2. Discuss new proposals&#10;3. Q&A session"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              Create Meeting
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

// Meeting Details Modal Component
const MeetingDetailsModal = ({ isOpen, onClose, meeting, members, attendances, activeTab, setActiveTab, onUpdate, onDelete, onUpdateAttendances, isAdmin, can }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showCallRegister, setShowCallRegister] = useState(false);
  const { data: workflow } = useWorkflowSettings();
  const canCallRegister = !!(isAdmin && workflow?.call_register_enabled && can && can(PERMISSIONS.EDIT_MEETING));
  const [editForm, setEditForm] = useState({});
  const [attendanceForm, setAttendanceForm] = useState({});
  const [minutesContent, setMinutesContent] = useState('');
  const [showMinutesPreview, setShowMinutesPreview] = useState(false);
  const [contributionForm, setContributionForm] = useState({});
  const [fineSettings, setFineSettings] = useState({
    absenceFine: 0,
    lateFine: 0,
    noRsvpFine: 0
  });
  const [actionItems, setActionItems] = useState([]);
  const [actionItemForm, setActionItemForm] = useState({
    title: '',
    description: '',
    assigned_to: '',
    due_date: ''
  });
  const [rsvps, setRsvps] = useState([]);
  const [rsvpSummary, setRsvpSummary] = useState({ yes: 0, no: 0, maybe: 0, no_response: 0 });
  const [myRsvp, setMyRsvp] = useState(null);

  // Refs for WYSIWYG editors
  const minutesEditorRef = useRef(null);
  const actionItemDescEditorRef = useRef(null);

  // Initialize forms when meeting changes
  useEffect(() => {
    if (meeting) {
      setEditForm({
        title: meeting.title,
        description: meeting.description || '',
        meeting_date: meeting.meeting_date,
        start_time: meeting.start_time,
        location: meeting.location || '',
        agenda: meeting.agenda || '',
        type: meeting.type || 'regular',
        status: meeting.status || 'scheduled',
        send_reschedule_sms: false,
        send_cancel_sms: false,
      });
      
      // Initialize minutes
      setMinutesContent(meeting.minutes || '');
      setTimeout(() => {
        if (minutesEditorRef.current) {
          minutesEditorRef.current.innerHTML = meeting.minutes || '';
        }
      }, 100);
      
      // Initialize fine settings
      if (meeting.fine_settings) {
        setFineSettings({
          absenceFine: meeting.fine_settings.absence_fine || 0,
          lateFine: meeting.fine_settings.late_fine || 0,
          noRsvpFine: meeting.fine_settings.no_rsvp_fine || 0
        });
      }
      
      // Initialize contribution form
      const initialContributions = {};
      members.forEach(member => {
        initialContributions[member.id] = {
          amount: meeting.contribution_amount || 0,
          notes: ''
        };
      });
      setContributionForm(initialContributions);
      
      // Initialize attendance form
      const initialAttendance = {};
      members.forEach(member => {
        const existing = attendances.find(a => a.member_id === member.id);
        initialAttendance[member.id] = existing ? {
          status: existing.status,
          arrival_time: existing.arrival_time || '',
          departure_time: existing.departure_time || '',
          notes: existing.notes || ''
        } : {
          status: 'accepted',
          arrival_time: '',
          departure_time: '',
          notes: ''
        };
      });
      setAttendanceForm(initialAttendance);
      
      // Load action items
      loadActionItems();
      
      // Load RSVPs
      loadRsvps();
    }
  }, [meeting, members, attendances]);

  const loadActionItems = async () => {
    if (!meeting) return;
    try {
      const response = await api.get(`/meetings/${meeting.id}/action-items`);
      setActionItems(response.data.data || []);
    } catch (error) {
      console.error('Failed to load action items:', error);
    }
  };

  const loadRsvps = async () => {
    if (!meeting) return;
    try {
      const [rsvpsResponse, summaryResponse] = await Promise.all([
        api.get(`/meetings/${meeting.id}/rsvps`),
        api.get(`/meetings/${meeting.id}/rsvp-summary`)
      ]);
      setRsvps(rsvpsResponse.data.data || []);
      setRsvpSummary(summaryResponse.data.data || { yes: 0, no: 0, maybe: 0, no_response: 0 });
      
      // Find current user's RSVP
      const currentUserId = JSON.parse(localStorage.getItem('user'))?.id;
      const userRsvp = rsvpsResponse.data.data?.find(r => r.member?.user_id === currentUserId);
      setMyRsvp(userRsvp);
    } catch (error) {
      console.error('Failed to load RSVPs:', error);
    }
  };

  // Rich text editor functions
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
  };

  const handleMinutesEditorChange = () => {
    if (minutesEditorRef.current) {
      setMinutesContent(minutesEditorRef.current.innerHTML);
    }
  };

  const handleActionItemDescChange = () => {
    if (actionItemDescEditorRef.current) {
      setActionItemForm(f => ({ ...f, description: actionItemDescEditorRef.current.innerHTML }));
    }
  };

  const handleCreateActionItem = async () => {
    if (!actionItemForm.title || !actionItemForm.assigned_to || !actionItemForm.due_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await api.post(`/meetings/${meeting.id}/action-items`, actionItemForm);
      toast.success('Action item created successfully');
      setActionItemForm({ title: '', description: '', assigned_to: '', due_date: '' });
      if (actionItemDescEditorRef.current) {
        actionItemDescEditorRef.current.innerHTML = '';
      }
      loadActionItems();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to create action item');
    }
  };

  const handleCompleteActionItem = async (itemId) => {
    try {
      await api.patch(`/action-items/${itemId}/complete`);
      toast.success('Action item marked as complete');
      loadActionItems();
    } catch (error) {
      toast.error('Failed to complete action item');
    }
  };

  const handleSubmitRsvp = async (response) => {
    try {
      const currentUserId = JSON.parse(localStorage.getItem('user'))?.id;
      const currentMember = members.find(m => m.user_id === currentUserId);
      
      if (!currentMember) {
        toast.error('Member not found');
        return;
      }

      await api.post(`/meetings/${meeting.id}/rsvp`, {
        member_id: currentMember.id,
        response
      });
      toast.success(`RSVP submitted: ${response}`);
      loadRsvps();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to submit RSVP');
    }
  };

  const handleSaveEdit = async () => {
    try {
      await onUpdate.mutateAsync({ id: meeting.id, data: editForm });
      toast.success('Meeting updated successfully');
      setIsEditing(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update meeting');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this meeting?')) {
      try {
        await onDelete.mutateAsync(meeting.id);
        toast.success('Meeting deleted successfully');
        onClose();
      } catch (error) {
        toast.error('Failed to delete meeting');
      }
    }
  };

  const handleAttendanceChange = (memberId, field, value) => {
    setAttendanceForm(prev => ({
      ...prev,
      [memberId]: { ...prev[memberId], [field]: value }
    }));
  };

  const handleSaveAttendances = async () => {
    try {
      const attendancesArray = Object.entries(attendanceForm).map(([memberId, data]) => ({
        meeting_id: meeting.id,
        member_id: parseInt(memberId),
        ...data
      }));
      
      await onUpdateAttendances.mutateAsync({
        meetingId: meeting.id,
        attendances: attendancesArray
      });
      
      toast.success('Attendance saved successfully');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save attendance');
    }
  };

  const getRSVPCounts = () => {
    const counts = { accepted: 0, declined: 0, pending: 0 };
    Object.values(attendanceForm).forEach(att => {
      if (att.status === 'accepted') counts.accepted++;
      else if (att.status === 'declined') counts.declined++;
      else counts.pending++;
    });
    return counts;
  };

  const handleSaveMinutes = async () => {
    try {
      await api.put(`/meetings/${meeting.id}/minutes`, {
        minutes: minutesContent
      });
      toast.success('Minutes saved successfully');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save minutes');
    }
  };

  const handleExportMinutesPDF = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      // Helper function to add new page if needed
      const checkPageBreak = (requiredSpace = 10) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Title
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text(meeting.title || 'Meeting Minutes', margin, yPosition);
      yPosition += 10;

      // Meeting details
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Date: ${new Date(meeting.meeting_date).toLocaleDateString()}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Time: ${meeting.meeting_time || meeting.start_time || 'TBD'}`, margin, yPosition);
      yPosition += 6;
      if (meeting.location) {
        doc.text(`Location: ${meeting.location}`, margin, yPosition);
        yPosition += 6;
      }
      yPosition += 5;

      // Separator line
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;

      // Minutes content
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Minutes:', margin, yPosition);
      yPosition += 8;

      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);

      // Convert HTML to plain text and handle formatting
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = minutesContent || 'No minutes recorded yet';
      const textContent = tempDiv.textContent || tempDiv.innerText || '';

      // Split text into lines that fit the page width
      const lines = doc.splitTextToSize(textContent, maxWidth);
      
      lines.forEach(line => {
        checkPageBreak();
        doc.text(line, margin, yPosition);
        yPosition += 6;
      });

      // Footer
      checkPageBreak(15);
      yPosition += 10;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 8;
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Generated on ${new Date().toLocaleString()}`, margin, yPosition);

      // Save the PDF
      const fileName = `${meeting.title.replace(/[^a-z0-9]/gi, '_')}_Minutes_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      toast.success('Minutes exported as PDF');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    }
  };

  const handleContributionChange = (memberId, field, value) => {
    setContributionForm(prev => ({
      ...prev,
      [memberId]: { ...prev[memberId], [field]: value }
    }));
  };

  const handleSaveContributions = async () => {
    try {
      const contributionsArray = Object.entries(contributionForm)
        .filter(([_, data]) => data.amount > 0)
        .map(([memberId, data]) => ({
          member_id: parseInt(memberId),
          amount: parseFloat(data.amount),
          notes: data.notes || `Contribution at meeting: ${meeting.title}`
        }));
      
      if (contributionsArray.length === 0) {
        toast.error('No contributions to record');
        return;
      }

      await api.post(`/meetings/${meeting.id}/contributions`, {
        contributions: contributionsArray
      });
      
      toast.success(`${contributionsArray.length} contributions recorded successfully`);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to record contributions');
    }
  };

  const handleApplyFines = async () => {
    if (!window.confirm('Are you sure you want to apply fines based on attendance? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await api.post(`/meetings/${meeting.id}/apply-fines`, {
        absence_fine: parseFloat(fineSettings.absenceFine) || 0,
        late_fine: parseFloat(fineSettings.lateFine) || 0,
        no_rsvp_fine: parseFloat(fineSettings.noRsvpFine) || 0
      });
      
      const summary = response.data.summary;
      toast.success(`${summary.total_fines} fines applied. Total amount: KES ${summary.total_amount}`);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to apply fines');
    }
  };

  const rsvpCounts = getRSVPCounts();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="huge">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            {isEditing ? (
              <input
                type="text"
                value={editForm.title}
                onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                className="text-2xl font-semibold text-gray-900 border-b-2 border-blue-500 focus:outline-none w-full"
              />
            ) : (
              <h2 className="text-2xl font-semibold text-gray-900">{meeting.title}</h2>
            )}
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
              <div className="flex items-center">
                <Calendar size={16} className="mr-1.5" />
                {new Date(meeting.meeting_date).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
              <div className="flex items-center">
                <Clock size={16} className="mr-1.5" />
                {meeting.start_time}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${
              meeting.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
              meeting.status === 'ongoing' ? 'bg-green-100 text-green-700' :
              meeting.status === 'completed' ? 'bg-gray-100 text-gray-700' :
              'bg-red-100 text-red-700'
            }`}>
              {meeting.status}
            </span>
            {isAdmin && can(PERMISSIONS.EDIT_MEETING) && (
              <>
                {isEditing ? (
                  <>
                    <button
                      onClick={handleSaveEdit}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                      title="Save"
                    >
                      <CheckCircle size={20} />
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                      title="Cancel"
                    >
                      <XCircle size={20} />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="Edit"
                  >
                    <Edit size={20} />
                  </button>
                )}
                {can(PERMISSIONS.DELETE_MEETING) && (
                  <button
                    onClick={handleDelete}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete"
                  >
                    <Trash2 size={20} />
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {['details', 'attendees', 'minutes', 'contributions', 'fines', ...(canCallRegister ? ['call-register'] : []), 'action-items', 'rsvp'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'action-items' ? 'Action Items' : 
                 tab === 'call-register' ? 'Call register' :
                 tab === 'rsvp' ? 'RSVP' :
                 tab.charAt(0).toUpperCase() + tab.slice(1)}
                {tab === 'attendees' && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                    {rsvpCounts.accepted}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px] max-h-[60vh] overflow-y-auto">
          {activeTab === 'details' && (
            <div className="space-y-6">
              {isEditing ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={editForm.description}
                      onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                      <input
                        type="date"
                        value={editForm.meeting_date}
                        onChange={e => setEditForm(f => ({ ...f, meeting_date: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                      <input
                        type="time"
                        value={editForm.start_time}
                        onChange={e => setEditForm(f => ({ ...f, start_time: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                    <input
                      type="text"
                      value={editForm.location}
                      onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Agenda</label>
                    <textarea
                      value={editForm.agenda}
                      onChange={e => setEditForm(f => ({ ...f, agenda: e.target.value }))}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={editForm.status}
                      onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="scheduled">Scheduled</option>
                      <option value="ongoing">Ongoing</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  {meeting.description && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Description</h3>
                      <p className="text-gray-600">{meeting.description}</p>
                    </div>
                  )}
                  {meeting.location && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <MapPin size={16} className="mr-2" />
                        Location
                      </h3>
                      <p className="text-gray-600">{meeting.location}</p>
                    </div>
                  )}
                  {meeting.agenda && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Agenda</h3>
                      <pre className="text-gray-600 whitespace-pre-wrap font-sans">{meeting.agenda}</pre>
                    </div>
                  )}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Type</h3>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700">
                      {meeting.type || 'Regular'}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'attendees' && (
            <div className="space-y-4">
              {/* RSVP Summary */}
              <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-3 kpi-grid">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-green-700">Going</span>
                    <CheckCircle size={20} className="text-green-600" />
                  </div>
                  <p className="text-2xl font-bold text-green-900 mt-2">{rsvpCounts.accepted}</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-red-700">Not Going</span>
                    <XCircle size={20} className="text-red-600" />
                  </div>
                  <p className="text-2xl font-bold text-red-900 mt-2">{rsvpCounts.declined}</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Maybe</span>
                    <HelpCircle size={20} className="text-gray-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{rsvpCounts.pending}</p>
                </div>
              </div>

              {/* Attendee List */}
              <div className="space-y-3">
                {members.map(member => {
                  const attendance = attendanceForm[member.id] || { status: 'accepted' };
                  return (
                    <div key={member.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">{member.name}</h4>
                          <p className="text-sm text-gray-500">{member.phone_number}</p>
                        </div>
                        <div className="flex gap-2">
                          {['accepted', 'declined', 'present', 'absent'].map(status => (
                            <button
                              key={status}
                              onClick={() => handleAttendanceChange(member.id, 'status', status)}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                attendance.status === status
                                  ? status === 'accepted' || status === 'present' ? 'bg-green-100 text-green-700' :
                                    status === 'declined' || status === 'absent' ? 'bg-red-100 text-red-700' :
                                    'bg-gray-100 text-gray-700'
                                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              {status === 'accepted' ? 'Going' : 
                               status === 'declined' ? 'Not Going' :
                               status === 'present' ? 'Present' : 'Absent'}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {(attendance.status === 'present' || attendance.status === 'accepted') && (
                        <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-100">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Arrival</label>
                            <input
                              type="time"
                              value={attendance.arrival_time || ''}
                              onChange={e => handleAttendanceChange(member.id, 'arrival_time', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Departure</label>
                            <input
                              type="time"
                              value={attendance.departure_time || ''}
                              onChange={e => handleAttendanceChange(member.id, 'departure_time', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      )}
                      
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                        <input
                          type="text"
                          value={attendance.notes || ''}
                          onChange={e => handleAttendanceChange(member.id, 'notes', e.target.value)}
                          placeholder="Optional notes..."
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleSaveAttendances}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Save Attendance
                </button>
              </div>
            </div>
          )}

          {activeTab === 'minutes' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Meeting Minutes</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleExportMinutesPDF}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                    title="Export as PDF"
                  >
                    <Download size={16} />
                    Export PDF
                  </button>
                  <button
                    onClick={() => setShowMinutesPreview(!showMinutesPreview)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {showMinutesPreview ? 'Edit' : 'Preview'}
                  </button>
                </div>
              </div>

              {showMinutesPreview ? (
                <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 min-h-[300px]">
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: minutesContent || '<p class="text-gray-400 italic">No minutes recorded yet</p>' }} />
                </div>
              ) : (
                <div>
                  {/* Toolbar */}
                  <div className="flex items-center gap-1 p-2 border border-gray-300 rounded-t-lg bg-gray-50">
                    <button
                      type="button"
                      onClick={() => execCommand('bold')}
                      className="p-2 hover:bg-gray-200 rounded"
                      title="Bold"
                    >
                      <Bold size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('italic')}
                      className="p-2 hover:bg-gray-200 rounded"
                      title="Italic"
                    >
                      <Italic size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('underline')}
                      className="p-2 hover:bg-gray-200 rounded"
                      title="Underline"
                    >
                      <Underline size={16} />
                    </button>
                    <div className="w-px h-6 bg-gray-300 mx-1" />
                    <button
                      type="button"
                      onClick={() => execCommand('insertUnorderedList')}
                      className="p-2 hover:bg-gray-200 rounded"
                      title="Bullet List"
                    >
                      <List size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('insertOrderedList')}
                      className="p-2 hover:bg-gray-200 rounded"
                      title="Numbered List"
                    >
                      <ListOrdered size={16} />
                    </button>
                    <div className="w-px h-6 bg-gray-300 mx-1" />
                    <button
                      type="button"
                      onClick={() => {
                        const url = prompt('Enter URL:');
                        if (url) execCommand('createLink', url);
                      }}
                      className="p-2 hover:bg-gray-200 rounded"
                      title="Insert Link"
                    >
                      <LinkIcon size={16} />
                    </button>
                    <select
                      onChange={(e) => execCommand('formatBlock', e.target.value)}
                      className="ml-2 px-2 py-1 border border-gray-300 rounded text-sm"
                      defaultValue=""
                    >
                      <option value="">Normal</option>
                      <option value="h1">Heading 1</option>
                      <option value="h2">Heading 2</option>
                      <option value="h3">Heading 3</option>
                    </select>
                  </div>
                  {/* Editor */}
                  <div
                    ref={minutesEditorRef}
                    contentEditable
                    onInput={handleMinutesEditorChange}
                    className="w-full px-3 py-2 border border-gray-300 border-t-0 rounded-b-lg focus:ring-2 focus:ring-blue-500 focus:outline-none min-h-[300px] bg-white"
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                  />
                </div>
              )}

              <div className="text-xs text-gray-500 mt-2">
                Use the toolbar to format your meeting minutes with headings, lists, and emphasis.
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleSaveMinutes}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Save Minutes
                </button>
              </div>
            </div>
          )}

          {activeTab === 'contributions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Meeting Contributions</h3>
                <div className="text-sm text-gray-600">
                  Expected: KES {meeting.contribution_amount || 0} per member
                </div>
              </div>

              <div className="space-y-3">
                {members.map(member => {
                  const contribution = contributionForm[member.id] || { amount: 0, notes: '' };
                  return (
                    <div key={member.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{member.name}</h4>
                          <p className="text-sm text-gray-500">{member.phone_number}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            value={contribution.amount}
                            onChange={e => handleContributionChange(member.id, 'amount', e.target.value)}
                            placeholder="Amount"
                            className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <input
                          type="text"
                          value={contribution.notes}
                          onChange={e => handleContributionChange(member.id, 'notes', e.target.value)}
                          placeholder="Optional notes..."
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleSaveContributions}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Record Contributions
                </button>
              </div>
            </div>
          )}

          {activeTab === 'call-register' && canCallRegister && (
            <div className="space-y-4" data-testid="call-register-tab">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-amber-900 mb-1">Call register</h3>
                <p className="text-xs text-amber-800">
                  Mark each member present, late, absent or on apology. Fines are charged automatically from the group's fine amounts
                  (change them in Settings &gt; Features) and taken from the member's project account first.
                </p>
              </div>
              <button type="button" onClick={() => setShowCallRegister(true)} className="px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700">
                {meeting?.call_register_completed_at ? 'Open the saved register' : 'Take the register'}
              </button>
              {showCallRegister && <CallRegisterModal meeting={meeting} onClose={() => setShowCallRegister(false)} />}
            </div>
          )}

          {activeTab === 'fines' && (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <h3 className="text-sm font-medium text-yellow-800 mb-2">
                  Fine Settings
                </h3>
                <p className="text-xs text-yellow-700">
                  Set fine amounts for attendance violations. Fines will be applied based on final attendance status.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Absence Fine (KES)
                  </label>
                  <input
                    type="number"
                    value={fineSettings.absenceFine}
                    onChange={e => setFineSettings(f => ({ ...f, absenceFine: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">For members marked absent</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Late Fine (KES)
                  </label>
                  <input
                    type="number"
                    value={fineSettings.lateFine}
                    onChange={e => setFineSettings(f => ({ ...f, lateFine: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">For members marked late</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    No RSVP Fine (KES)
                  </label>
                  <input
                    type="number"
                    value={fineSettings.noRsvpFine}
                    onChange={e => setFineSettings(f => ({ ...f, noRsvpFine: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">For members who didn't RSVP</p>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Preview</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>• Absent members will be fined KES {fineSettings.absenceFine || 0}</p>
                  <p>• Late members will be fined KES {fineSettings.lateFine || 0}</p>
                  <p>• Members without RSVP will be fined KES {fineSettings.noRsvpFine || 0}</p>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleApplyFines}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  Apply Fines Now
                </button>
              </div>
            </div>
          )}

          {activeTab === 'action-items' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h3 className="text-lg font-medium text-blue-900 mb-2">Create Action Item</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      type="text"
                      value={actionItemForm.title}
                      onChange={e => setActionItemForm(f => ({ ...f, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Action item title"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <div>
                      {/* Toolbar */}
                      <div className="flex items-center gap-1 p-2 border border-gray-300 rounded-t-lg bg-gray-50">
                        <button
                          type="button"
                          onClick={() => execCommand('bold')}
                          className="p-2 hover:bg-gray-200 rounded"
                          title="Bold"
                        >
                          <Bold size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => execCommand('italic')}
                          className="p-2 hover:bg-gray-200 rounded"
                          title="Italic"
                        >
                          <Italic size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => execCommand('underline')}
                          className="p-2 hover:bg-gray-200 rounded"
                          title="Underline"
                        >
                          <Underline size={16} />
                        </button>
                        <div className="w-px h-6 bg-gray-300 mx-1" />
                        <button
                          type="button"
                          onClick={() => execCommand('insertUnorderedList')}
                          className="p-2 hover:bg-gray-200 rounded"
                          title="Bullet List"
                        >
                          <List size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => execCommand('insertOrderedList')}
                          className="p-2 hover:bg-gray-200 rounded"
                          title="Numbered List"
                        >
                          <ListOrdered size={16} />
                        </button>
                        <div className="w-px h-6 bg-gray-300 mx-1" />
                        <button
                          type="button"
                          onClick={() => {
                            const url = prompt('Enter URL:');
                            if (url) execCommand('createLink', url);
                          }}
                          className="p-2 hover:bg-gray-200 rounded"
                          title="Insert Link"
                        >
                          <LinkIcon size={16} />
                        </button>
                      </div>
                      {/* Editor */}
                      <div
                        ref={actionItemDescEditorRef}
                        contentEditable
                        onInput={handleActionItemDescChange}
                        className="w-full px-3 py-2 border border-gray-300 border-t-0 rounded-b-lg focus:ring-2 focus:ring-blue-500 focus:outline-none min-h-[80px] bg-white"
                        style={{ maxHeight: '200px', overflowY: 'auto' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assign To *</label>
                    <select
                      value={actionItemForm.assigned_to}
                      onChange={e => setActionItemForm(f => ({ ...f, assigned_to: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select member</option>
                      {members.map(member => (
                        <option key={member.id} value={member.id}>{member.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
                    <input
                      type="date"
                      value={actionItemForm.due_date}
                      onChange={e => setActionItemForm(f => ({ ...f, due_date: e.target.value }))}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-4">
                  <button
                    onClick={handleCreateActionItem}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Create Action Item
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {actionItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No action items yet. Create one above.
                  </div>
                ) : (
                  actionItems.map(item => {
                    const isOverdue = new Date(item.due_date) < new Date() && item.status === 'pending';
                    return (
                      <div key={item.id} className={`border rounded-lg p-4 ${
                        item.status === 'completed' ? 'bg-green-50 border-green-200' :
                        isOverdue ? 'bg-red-50 border-red-200' :
                        'bg-white border-gray-200'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className={`font-medium ${
                                item.status === 'completed' ? 'text-green-900 line-through' :
                                isOverdue ? 'text-red-900' :
                                'text-gray-900'
                              }`}>
                                {item.title}
                              </h4>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                item.status === 'completed' ? 'bg-green-100 text-green-700' :
                                isOverdue ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {item.status === 'completed' ? 'Completed' :
                                 isOverdue ? 'Overdue' : 'Pending'}
                              </span>
                            </div>
                            {item.description && (
                              <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                              <span>Assigned to: {item.assignee?.name}</span>
                              <span>Due: {new Date(item.due_date).toLocaleDateString()}</span>
                            </div>
                          </div>
                          {item.status === 'pending' && (
                            <button
                              onClick={() => handleCompleteActionItem(item.id)}
                              className="ml-4 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                              Mark Complete
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {activeTab === 'rsvp' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 mb-6 sm:grid-cols-4 kpi-grid">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-green-700">Yes</span>
                    <CheckCircle size={20} className="text-green-600" />
                  </div>
                  <p className="text-2xl font-bold text-green-900 mt-2">{rsvpSummary.yes}</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-red-700">No</span>
                    <XCircle size={20} className="text-red-600" />
                  </div>
                  <p className="text-2xl font-bold text-red-900 mt-2">{rsvpSummary.no}</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-yellow-700">Maybe</span>
                    <HelpCircle size={20} className="text-yellow-600" />
                  </div>
                  <p className="text-2xl font-bold text-yellow-900 mt-2">{rsvpSummary.maybe}</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">No Response</span>
                    <HelpCircle size={20} className="text-gray-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{rsvpSummary.no_response}</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h3 className="text-sm font-medium text-blue-900 mb-3">Your RSVP</h3>
                {myRsvp ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-blue-700">
                      You responded: <strong>{myRsvp.response}</strong> on {new Date(myRsvp.responded_at).toLocaleDateString()}
                    </span>
                    <span className="text-xs text-blue-600">Click below to change</span>
                  </div>
                ) : (
                  <p className="text-sm text-blue-700">You haven't responded yet</p>
                )}
                <div className="flex gap-3 mt-3">
                  <button
                    onClick={() => handleSubmitRsvp('yes')}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                      myRsvp?.response === 'yes'
                        ? 'bg-green-600 text-white'
                        : 'bg-white text-green-700 border border-green-300 hover:bg-green-50'
                    }`}
                  >
                    Yes, I'll attend
                  </button>
                  <button
                    onClick={() => handleSubmitRsvp('no')}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                      myRsvp?.response === 'no'
                        ? 'bg-red-600 text-white'
                        : 'bg-white text-red-700 border border-red-300 hover:bg-red-50'
                    }`}
                  >
                    No, I can't attend
                  </button>
                  <button
                    onClick={() => handleSubmitRsvp('maybe')}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                      myRsvp?.response === 'maybe'
                        ? 'bg-yellow-600 text-white'
                        : 'bg-white text-yellow-700 border border-yellow-300 hover:bg-yellow-50'
                    }`}
                  >
                    Maybe
                  </button>
                </div>
              </div>

              {isAdmin && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">All Responses</h3>
                  <div className="space-y-2">
                    {['yes', 'no', 'maybe'].map(response => {
                      const responseRsvps = rsvps.filter(r => r.response === response);
                      if (responseRsvps.length === 0) return null;
                      return (
                        <div key={response} className="border border-gray-200 rounded-lg p-3">
                          <h4 className="text-sm font-medium text-gray-700 mb-2 capitalize">
                            {response} ({responseRsvps.length})
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {responseRsvps.map(rsvp => (
                              <span key={rsvp.id} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                                {rsvp.member?.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default MeetingsPageNew;
